import { FC } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ZendeskArticle } from 'interfaces/ZendeskArticle';
import { Box, Button, Container, Theme, Typography, useMediaQuery } from '@mui/material';
import { styled } from '@mui/material/styles';
import Grid from '@mui/material/Unstable_Grid2';

import { usePublicArticle } from 'hooks/queries/useArticles';
import { useToverHead } from 'hooks/useToverHead';
import ArticleMetaData from 'components/mytover/content/ArticleMetaData';
import ArticleSectionMenu from 'components/mytover/content/ArticleSectionMenu';
import CenteredCircularProgress from 'components/page/CenteredCircularProgress';
import { DefaultLayout } from 'components/page/layouts/DefaultLayout';
import PageHeaderSection from 'components/page/PageHeaderSection';
import PageSectionContainer from 'components/page/PageSectionContainer';
import ArticleSections from 'components/page/sections/ArticleSections';

interface ArticleContainerProps {
  identifier?: string;
  // Already resolved elsewhere (e.g. ContentPage) — skip the internal fetch.
  article?: ZendeskArticle;
}

const StyledArticle = styled('div')(({ theme }) => ({
  '& table': {
    [theme.breakpoints.only('xs')]: {
      'tableLayout': 'fixed',
      'width': '100%',
      '& td': {
        overflowX: 'auto',
      },
    },
  },
}));

const ArticleContainer: FC<ArticleContainerProps> = ({ identifier, article: preloadedArticle }) => {
  const { t, i18n } = useTranslation();
  const { id: idParam, locale } = useParams();
  const onMobileAndTablet = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));

  const articleId = preloadedArticle ? '' : identifier ?? idParam ?? '';
  const { data: fetchedArticle, isLoading, error } = usePublicArticle(articleId, locale ?? i18n.language);
  const article = preloadedArticle ?? fetchedArticle;

  useToverHead({
    title: article?.title ?? '',
    description: article?.seo?.meta_description ?? undefined,
    alternateLinks: article?.alt_links,
  });

  // Why the article can't be read: 403 means the reader is signed in but the My Tover
  // service their Tovertafel would carry is what's missing, so saying "sign in" would
  // send them down a road that cannot help.
  const refusedBecause = error ? ((error as any)?.response?.status === 403 ? 'mytover' : 'signin') : undefined;

  const createMarkup = (content?: any) => ({ __html: content || article!.body! });

  return (
    <DefaultLayout>
      <Container
        maxWidth="xl"
        sx={{
          '& a[name]': {
            display: 'block',
            position: 'relative',
            top: -100,
            visibility: 'hidden',
          },
        }}
      >
        {isLoading ? (
          <CenteredCircularProgress />
        ) : article ? (
          <Grid container sx={{ pb: 8 }}>
            <Grid xs={12} md={3} container component="aside" direction="column">
              <Grid sx={{ ml: onMobileAndTablet ? 0 : 3 }}>
                <ArticleSectionMenu slug={article?.slug} category={article?.category?.id} />
              </Grid>
            </Grid>

            <Grid xs={12} md={8} paddingX={0}>
              <PageHeaderSection titleId="article-title" title={article?.title} crumbs={article?.breadcrumbs} />

              <PageSectionContainer sx={{ py: 0 }}>
                <ArticleMetaData article={article} />
              </PageSectionContainer>

              {/* Translation is per language: without one in the requested language the
                  article has no content, which is worth saying rather than showing an
                  empty page. */}
              {!article.lang ? (
                <PageSectionContainer sx={{ pb: 4 }}>
                  <Typography id="article-unavailable">{t('article.unavailable-in-language')}</Typography>
                </PageSectionContainer>
              ) : article.sections ? (
                <ArticleSections sections={article.sections} />
              ) : (
                article.body && (
                  <PageSectionContainer sx={{ pb: 4 }}>
                    <StyledArticle id="article-body" dangerouslySetInnerHTML={createMarkup()} />
                  </PageSectionContainer>
                )
              )}
            </Grid>
          </Grid>
        ) : refusedBecause === 'mytover' ? (
          <>
            <PageHeaderSection title={t('article.locked.title')} />

            <PageSectionContainer>
              <Box sx={{ mb: 4 }}>
                <Typography id="article-mytover-required">{t('error.403.no-mytover')}</Typography>
              </Box>

              <Box>
                <Button color="secondary" component={RouterLink} to="/request-invite">
                  {t('opl.dialog.actions.request')}
                </Button>
              </Box>
            </PageSectionContainer>
          </>
        ) : (
          <>
            <PageHeaderSection title={t('intro.module.signin')} />

            <PageSectionContainer>
              <Box sx={{ mb: 4 }}>
                <Typography id="article-signin-required">{t('opl.dialog.body')}</Typography>
              </Box>

              <Box>
                <Button color="secondary" component={RouterLink} to="/request-invite" sx={{ mr: 2 }}>
                  {t('opl.dialog.actions.request')}
                </Button>
                <Button color="primary" component={RouterLink} to="/signin">
                  {t('opl.dialog.actions.signin')}
                </Button>
              </Box>
            </PageSectionContainer>
          </>
        )}
      </Container>
    </DefaultLayout>
  );
};

export default ArticleContainer;
